/**
 * VOICE.NRW — Premium SEO Landing Page
 *
 * Standalone page, NOT wrapped by PublicLayout. Has its own dark Apple/
 * Leica/Porsche-inspired design system, font loading, SEO meta and JSON-LD.
 * Not linked from main nav or footer — only reachable via direct URL
 * /mailboxansage-bestellen.
 *
 * To replace the placeholder MP3 demos with the real files, edit DEMOS
 * (search for "PLATZHALTER" in this file).
 */
import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Phone, ShieldCheck, Mic2, Clock, Award, Headphones, Play, Pause,
  Briefcase, Stethoscope, Building2, Scale, Calculator, Home, Megaphone, Factory,
  PenLine, Brush, Disc3, FileDown,
} from "lucide-react";

const ORDER_URL = "https://voicenrw.de/shop";
const SPEAKER_PORTRAIT = "https://voicenrw.de/studio/thomas_portrait.webp";
const STUDIO_BG = "https://voicenrw.de/studio/hero_mic_bg.jpg";

/* ------------------------------------------------------------------------ */
/* Audio demos — replace these URLs with your real MP3s on voicenrw.de.    */
/* PLATZHALTER: bitte echte Demo-MP3-URLs eintragen.                       */
/* ------------------------------------------------------------------------ */
const DEMOS = [
  { key: "tv",      title: "TV-Trailer",          subtitle: "RTL · Sport1 · DSDS",          url: "https://voicenrw.de/audio/demo-tv.mp3" },
  { key: "kino",    title: "Kino-Spot",           subtitle: "Werbung · Dolby-Mastering",     url: "https://voicenrw.de/audio/demo-kino.mp3" },
  { key: "radio",   title: "Radio-Werbung",       subtitle: "30s Spot · gemastert",          url: "https://voicenrw.de/audio/demo-radio.mp3" },
  { key: "werbung", title: "Imagefilm",           subtitle: "Markenkommunikation",           url: "https://voicenrw.de/audio/demo-werbung.mp3" },
  { key: "mailbox", title: "Mailboxansage",       subtitle: "Voicemail · 24h Lieferung",     url: "https://voicenrw.de/audio/demo-mailbox.mp3" },
  { key: "telefon", title: "Telefonansage",       subtitle: "Unternehmen · VoIP-tauglich",   url: "https://voicenrw.de/audio/demo-telefon.mp3" },
];

const CLIENTS = [
  { name: "RTL",                src: "https://voicenrw.de/clients/rtl.png" },
  { name: "Sport1",             src: "https://voicenrw.de/clients/sport1.png" },
  { name: "DSDS",               src: "https://voicenrw.de/clients/dsds.png" },
  { name: "Wetten, dass..?",    src: "https://voicenrw.de/clients/wetten-dass.png" },
  { name: "Wer stiehlt mir die Show?", src: "https://voicenrw.de/clients/wer-stiehlt-mir-die-show.jpg" },
  { name: "Mastercard",         src: "https://voicenrw.de/clients/mastercard.png" },
  { name: "McDonald’s",         src: "https://voicenrw.de/clients/mcdonalds.png" },
  { name: "Heide Park Resort",  src: "https://voicenrw.de/clients/heidepark.jpg" },
  { name: "ONCE",               src: "https://voicenrw.de/clients/once.png" },
  { name: "Handelsverband Deutschland", src: "https://voicenrw.de/clients/hde.svg" },
];

const INDUSTRIES = [
  { icon: Factory, key: "handwerk", title: "Handwerk", text: "Eine klare Ansage, die Vertrauen schafft und Anrufer professionell empfängt — auch wenn Sie gerade auf der Baustelle sind." },
  { icon: Stethoscope, key: "aerzte", title: "Ärzte", text: "Patientinnen und Patienten hören eine ruhige, freundliche Stimme. Inklusive Notfall-Hinweis, Sprechzeiten und Vertretungsregelung." },
  { icon: Briefcase, key: "dienstleister", title: "Dienstleister", text: "Eine Ansage, die Ihr Leistungsversprechen sofort hörbar macht — ohne Floskeln, ohne Massenware." },
  { icon: Scale, key: "kanzleien", title: "Kanzleien", text: "Seriös, präzise, vertrauensvoll. Die richtige Stimme für Mandanten und sensible Themen." },
  { icon: Calculator, key: "steuerberater", title: "Steuerberater", text: "Diskret und professionell. Für Hauptzeit, Urlaub, Steuersaison und Außer-Haus-Tage." },
  { icon: Home, key: "makler", title: "Immobilienmakler", text: "Hochwertige Begrüßung mit Markenpersönlichkeit — vom ersten Klingeln an Premium." },
  { icon: Megaphone, key: "agenturen", title: "Agenturen", text: "Für Sie und Ihre Kunden: Voiceover, Image-Spots, Telefonansagen — schnell, sauber, sendefähig." },
  { icon: Building2, key: "mittelstand", title: "Mittelstand", text: "Mehrstufige IVR, Warteschleife, Außer-Haus-Ansage — eine konsistente Markenstimme über alle Kanäle." },
];

const TRUST = [
  { icon: Mic2,          label: "Echter Sprecher",      sub: "Keine KI-Massenware" },
  { icon: Headphones,    label: "Tonstudio-Qualität",   sub: "Broadcast-Standard" },
  { icon: Award,         label: "TV · Kino · Radio",    sub: "Über 30 Jahre Mikro" },
  { icon: Clock,         label: "24 h Lieferzeit",      sub: "Express möglich" },
  { icon: FileDown,      label: "Fertige MP3-Datei",    sub: "Auch WAV / ALAW" },
];

const STEPS = [
  { n: "01", icon: PenLine, title: "Text senden",      text: "Sie senden uns Ihren Text — oder lassen ihn von uns formulieren." },
  { n: "02", icon: Brush,   title: "Stil wählen",      text: "Sympathisch, sachlich oder markant. Wir besprechen Tempo und Tonlage." },
  { n: "03", icon: Disc3,   title: "Studioaufnahme",   text: "Aufnahme im Tonstudio, Schnitt, EQ und Mastering nach EBU-R128." },
  { n: "04", icon: FileDown,title: "MP3 erhalten",     text: "Sendefertige Datei per Mail. Optional WAV oder ALAW für Telefonanlagen." },
];

const FAQS = [
  { q: "Wie kann ich eine Mailboxansage bestellen?", a: "Sie senden uns Ihren Wunschtext, wählen einen Stil und erhalten innerhalb von 24 Stunden Ihre fertig produzierte Mailboxansage als MP3 per E-Mail. Auf Wunsch übernehmen wir auch das Texten." },
  { q: "Wie kann ich eine Telefonansage bestellen?", a: "Wählen Sie zwischen Begrüßung, Warteschleife, IVR-Auswahlmenü oder Außer-Haus-Ansage. Wir besprechen Tonlage und Tempo, nehmen im Studio auf und liefern die fertige Datei im passenden Format — MP3, WAV oder ALAW für Telefonanlagen." },
  { q: "Wird die Ansage von einem echten Sprecher aufgenommen?", a: "Ja. Jede Ansage wird persönlich von Thomas Dickenbrok im Tonstudio in Herford eingesprochen — keine KI, keine Sprach-Synthese. Sie hören echten Atem, echte Pausen und echte Persönlichkeit." },
  { q: "Ist die Ansage auch für Unternehmen geeignet?", a: "Ja. Unsere Ansagen werden bevorzugt von Handwerksbetrieben, Arztpraxen, Kanzleien, Steuerberatern, Immobilienmaklern, Agenturen und mittelständischen Unternehmen eingesetzt — bis hin zu Konzernen mit mehrstufigen IVR-Anlagen." },
  { q: "Bekomme ich die Datei als MP3?", a: "Standardmäßig ja. Auf Wunsch zusätzlich als WAV (CD-Qualität) oder ALAW 8 kHz für klassische Telefonanlagen." },
  { q: "Kann ich die Ansage für VoIP oder Telefonanlage nutzen?", a: "Selbstverständlich. Wir liefern in allen gängigen Formaten — kompatibel mit Mobilbox, VoIP, SIP-Anlagen, klassischen TK-Systemen und allen großen Telefonanbietern." },
  { q: "Kann VOICE.NRW auch den Text schreiben?", a: "Ja. Wenn Sie nur eine Idee haben, formulieren wir den Text für Sie — klar, sympathisch und auf Ihre Branche zugeschnitten. Vorlagen für Handwerk, Praxis, Kanzlei und Unternehmen sind vorhanden." },
  { q: "Warum ist eine echte Stimme besser als KI?", a: "Eine echte Stimme trägt Wärme, Pausen, Atem und Kontextverständnis. KI-Stimmen werden zunehmend von Anrufern als künstlich erkannt — das wirkt billig und unpersönlich. Eine professionelle Stimme ist Ihr erster Eindruck. Genau dort sollten Sie nicht sparen." },
  { q: "Wie schnell wird geliefert?", a: "Standard: innerhalb von 24 Stunden ab Texteingang. Express auf Anfrage. Lieferung per E-Mail mit Download-Link." },
  { q: "Für welche Branchen eignet sich eine professionelle Ansage?", a: "Praktisch jede Branche profitiert: Handwerk, Ärzte, Kanzleien, Steuerberater, Immobilienmakler, Dienstleister, Agenturen, B2B-Unternehmen, Onlineshops mit Service-Hotline, Kommunen und Vereine." },
];

/* ---------------------- Audio player with waveform ---------------------- */
function WaveformPlayer({ demo, isActive, onPlay }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!isActive && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [isActive]);

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      try {
        onPlay(demo.key);
        await audioRef.current.play();
        setPlaying(true);
      } catch {
        setErrored(true);
      }
    }
  };

  // Generate a stable pseudo-random waveform per demo (so it doesn't flicker)
  const bars = React.useMemo(() => {
    const seed = demo.key.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return Array.from({ length: 48 }, (_, i) => {
      const x = Math.sin((seed + i * 7) * 0.91) * 0.5 + Math.cos((seed + i) * 0.37) * 0.3;
      return 0.25 + (Math.abs(x) % 0.75);
    });
  }, [demo.key]);

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div className={`group relative rounded-2xl border transition-all ${
      isActive
        ? "border-[#d4a154]/40 bg-gradient-to-br from-[#181715] to-[#0e0d0b]"
        : "border-white/[0.06] bg-[#0f0f0f] hover:border-white/[0.12]"
    }`}>
      <audio
        ref={audioRef}
        src={demo.url}
        preload="none"
        onLoadedMetadata={(e) => { setDuration(e.currentTarget.duration || 0); setLoaded(true); }}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onError={() => setErrored(true)}
        crossOrigin="anonymous"
      />
      <div className="flex items-center gap-5 p-5 sm:p-6">
        <button
          onClick={toggle}
          aria-label={playing ? `${demo.title} pausieren` : `${demo.title} abspielen`}
          data-testid={`demo-play-${demo.key}`}
          className={`flex-shrink-0 h-14 w-14 rounded-full flex items-center justify-center transition-all ${
            playing
              ? "bg-[#d4a154] text-[#0a0a0a] shadow-[0_0_30px_-6px_#d4a154]"
              : "bg-white/5 text-white border border-white/10 group-hover:bg-white/10"
          }`}
        >
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div>
              <div className="text-white font-medium text-base sm:text-lg leading-tight">{demo.title}</div>
              <div className="text-xs text-white/40 mt-0.5 tracking-wide">{demo.subtitle}</div>
            </div>
            <div className="text-[11px] tabular-nums text-white/40 hidden sm:block">
              {loaded ? fmtTime(progress) + " / " + fmtTime(duration) : "—"}
            </div>
          </div>
          <div className="h-10 flex items-center gap-[3px] relative">
            {bars.map((h, i) => {
              const barIsLit = (i / bars.length) * 100 <= pct;
              return (
                <span
                  key={i}
                  style={{ height: `${h * 100}%` }}
                  className={`flex-1 rounded-full transition-colors duration-150 ${
                    barIsLit
                      ? "bg-[#d4a154]"
                      : playing ? "bg-white/15" : "bg-white/[0.08] group-hover:bg-white/[0.12]"
                  }`}
                />
              );
            })}
          </div>
          {errored && (
            <div className="text-[11px] text-white/40 mt-2">Hörprobe lädt — gleich wieder probieren oder im <a href={ORDER_URL} className="underline">Shop</a> direkt anhören.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

/* ---------------------- JSON-LD ---------------------- */
function injectJsonLd(data, id) {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function useVoiceNrwSEO() {
  useEffect(() => {
    const PAGE_URL = "https://voicenrw.de/mailboxansage-bestellen";
    const title = "Mailboxansage bestellen & Telefonansage bestellen | VOICE.NRW";
    const desc = "Mailboxansage bestellen oder Telefonansage bestellen bei VOICE.NRW. Professionelle Ansagen von Thomas Dickenbrok, bekannt aus TV, Kino, Radio und Werbung.";
    document.title = title;
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => { if (k !== "content") el.setAttribute(k, v); });
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };
    ensureMeta('meta[name="description"]',                  { name: "description", content: desc });
    ensureMeta('meta[property="og:title"]',                 { property: "og:title", content: title });
    ensureMeta('meta[property="og:description"]',           { property: "og:description", content: desc });
    ensureMeta('meta[property="og:type"]',                  { property: "og:type", content: "website" });
    ensureMeta('meta[property="og:url"]',                   { property: "og:url", content: PAGE_URL });
    ensureMeta('meta[property="og:image"]',                 { property: "og:image", content: "https://voicenrw.de/og-default.png" });
    ensureMeta('meta[name="twitter:card"]',                 { name: "twitter:card", content: "summary_large_image" });
    ensureMeta('meta[name="twitter:title"]',                { name: "twitter:title", content: title });
    ensureMeta('meta[name="twitter:description"]',          { name: "twitter:description", content: desc });
    ensureMeta('meta[name="robots"]',                       { name: "robots", content: "index,follow,max-image-preview:large" });
    // Canonical
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", PAGE_URL);

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://voicenrw.de/#organization",
      "name": "VOICE.NRW",
      "url": "https://voicenrw.de",
      "logo": "https://voicenrw.de/og-default.png",
      "address": { "@type": "PostalAddress", "addressLocality": "Herford", "addressRegion": "NRW", "addressCountry": "DE" },
      "founder": { "@id": "https://voicenrw.de/#thomas" },
    }, "ld-org");

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": "https://voicenrw.de/#thomas",
      "name": "Thomas Dickenbrok",
      "jobTitle": "Sprecher · Moderator · Tonstudio-Inhaber",
      "worksFor": { "@id": "https://voicenrw.de/#organization" },
      "image": SPEAKER_PORTRAIT,
      "knowsAbout": ["Telefonansage","Mailboxansage","Voiceover","Sprecher","TV-Trailer","Kino-Werbung","Radiowerbung","Tonstudio","Podcast"],
    }, "ld-person");

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Mailboxansage bestellen",
      "serviceType": "Professionelle Mailboxansage",
      "provider": { "@id": "https://voicenrw.de/#organization" },
      "areaServed": { "@type": "Country", "name": "Deutschland" },
      "audience": { "@type": "BusinessAudience", "audienceType": "Handwerk, Ärzte, Kanzleien, Steuerberater, Immobilienmakler, Agenturen, KMU" },
      "offers": { "@type": "Offer", "url": ORDER_URL, "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
    }, "ld-service-mailbox");

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Telefonansage bestellen",
      "serviceType": "Professionelle Telefonansage für Unternehmen",
      "provider": { "@id": "https://voicenrw.de/#organization" },
      "areaServed": { "@type": "Country", "name": "Deutschland" },
      "audience": { "@type": "BusinessAudience", "audienceType": "Unternehmen, Praxen, Kanzleien, Telefonanlagen, VoIP" },
      "offers": { "@type": "Offer", "url": ORDER_URL, "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
    }, "ld-service-telefon");

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question", "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    }, "ld-faq");

    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "VOICE.NRW", "item": "https://voicenrw.de/" },
        { "@type": "ListItem", "position": 2, "name": "Mailboxansage bestellen", "item": PAGE_URL },
      ],
    }, "ld-breadcrumb");

    return () => {
      ["ld-org","ld-person","ld-service-mailbox","ld-service-telefon","ld-faq","ld-breadcrumb"].forEach((id) => {
        const el = document.getElementById(id); if (el) el.remove();
      });
    };
  }, []);
}

/* ---------------------- The page ---------------------- */
export default function VoiceNrwLanding() {
  useVoiceNrwSEO();
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="voicenrw-landing min-h-screen bg-[#0a0a0a] text-white antialiased" data-testid="voicenrw-landing">
      <style>{`
        .voicenrw-landing {
          font-family: 'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          letter-spacing: -0.005em;
        }
        .voicenrw-landing .display { font-family: 'Playfair Display', Georgia, serif; letter-spacing: -0.02em; }
        .voicenrw-landing ::selection { background: #d4a154; color: #0a0a0a; }
        @keyframes vnrwFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .voicenrw-landing .float { animation: vnrwFloat 7s ease-in-out infinite; }
      `}</style>

      {/* Slim top bar — no navigation, only brand */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-7 flex items-center justify-between">
        <a href="https://voicenrw.de" className="inline-flex items-center gap-2 group" aria-label="VOICE.NRW Startseite">
          <span className="h-2 w-2 rounded-full bg-[#d4a154]" />
          <span className="text-sm tracking-[0.25em] uppercase text-white/80 group-hover:text-white transition-colors">VOICE.NRW</span>
        </a>
        <a href={ORDER_URL} data-testid="topbar-order" className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white inline-flex items-center gap-2">
          Bestellen <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* ============================ 1. HERO ============================ */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 70%, rgba(10,10,10,1) 100%), url(${STUDIO_BG})`,
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-40 h-[34rem] w-[34rem] rounded-full -z-10 blur-3xl opacity-[0.18]"
          style={{ background: "radial-gradient(circle, #d4a154 0%, transparent 70%)" }}
        />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 sm:pt-24 lg:pt-32 pb-20 lg:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-6">
              <span className="h-px w-8 bg-[#d4a154]" />
              Premium Voice · Studio Herford
            </p>
            <h1 className="display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.98] mb-8">
              Mailboxansage bestellen.<br />
              <span className="italic text-[#d4a154]">Telefonansage bestellen.</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl mb-10">
              Professionelle Ansagen mit echter Stimme, Studioqualität und der Erfahrung aus
              TV, Kino, Radio und Werbung. Für Handwerk, Ärzte, Kanzleien, Dienstleister und Unternehmen.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href={ORDER_URL}
                data-testid="hero-cta-order"
                className="inline-flex items-center gap-2 bg-[#d4a154] text-[#0a0a0a] font-medium px-7 py-4 rounded-full hover:bg-[#e2b163] transition-colors text-[15px]"
              >
                Ansage jetzt bestellen <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#hoerbeispiele"
                data-testid="hero-cta-demos"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-4 rounded-full hover:bg-white/[0.05] transition-colors text-[15px]"
              >
                <Headphones className="h-4 w-4" /> Hörbeispiele anhören
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-[#d4a154]" /> Echter Sprecher</span>
              <span>·</span>
              <span>EBU-R128 gemastert</span>
              <span>·</span>
              <span>24 h Lieferzeit</span>
              <span>·</span>
              <span>seit 2004</span>
            </div>
          </div>

          {/* Speaker portrait card + 2 inline demos */}
          <div className="relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto rounded-3xl overflow-hidden border border-white/[0.08] shadow-[0_25px_80px_-20px_rgba(0,0,0,0.8)] float">
              <img
                src={SPEAKER_PORTRAIT}
                alt="Thomas Dickenbrok — Sprecher VOICE.NRW"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.85) 100%)" }} />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4a154] mb-1">Thomas Dickenbrok</div>
                <div className="display text-2xl text-white leading-tight">30 Jahre Mikrofon.<br />Eine Stimme.</div>
              </div>
            </div>

            <div className="mt-6 max-w-md mx-auto lg:ml-auto space-y-3">
              {DEMOS.slice(0, 2).map((d) => (
                <WaveformPlayer key={d.key} demo={d} isActive={activeKey === d.key} onPlay={setActiveKey} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ 2. TRUST ============================ */}
      <section className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-end">
            <div>
              <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Erster Eindruck</p>
              <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05]">
                Die Stimme für<br />Ihren ersten Eindruck.
              </h2>
            </div>
            <p className="text-lg text-white/65 leading-relaxed">
              Wenn niemand ans Telefon geht, spricht Ihre Ansage für Sie. Genau dann sollte sie
              professionell, sympathisch und glaubwürdig klingen — nicht nach Vorlage, nicht nach KI,
              sondern nach Persönlichkeit.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/[0.06] mt-16 lg:mt-20 rounded-2xl overflow-hidden">
            {TRUST.map((t) => (
              <div key={t.label} className="bg-[#0a0a0a] p-6 sm:p-7 hover:bg-white/[0.02] transition-colors">
                <t.icon className="h-5 w-5 text-[#d4a154] mb-5" strokeWidth={1.6} />
                <div className="text-white font-medium text-[15px] leading-tight mb-1.5">{t.label}</div>
                <div className="text-xs text-white/45 tracking-wide">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 3. INDUSTRIES ============================ */}
      <section className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="max-w-3xl mb-14 lg:mb-18">
            <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Branchen</p>
            <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05]">
              Professionelle Ansagen<br />
              <span className="italic">für Unternehmen, Praxen und Dienstleister.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] rounded-2xl overflow-hidden">
            {INDUSTRIES.map((i) => (
              <div
                key={i.key}
                data-testid={`industry-${i.key}`}
                className="bg-[#0a0a0a] p-7 group hover:bg-white/[0.02] transition-colors"
              >
                <i.icon className="h-5 w-5 text-[#d4a154] mb-5" strokeWidth={1.6} />
                <h3 className="display text-2xl text-white mb-3 leading-tight">{i.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 4. THOMAS ============================ */}
      <section className="border-t border-white/[0.06] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -left-20 h-[28rem] w-[28rem] rounded-full -z-10 blur-3xl opacity-[0.12]"
          style={{ background: "radial-gradient(circle, #d4a154 0%, transparent 70%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Die Stimme</p>
            <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05] mb-8">
              Eine Stimme aus<br /><span className="italic">TV, Kino, Radio und Werbung.</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Thomas Dickenbrok steht seit über 30 Jahren professionell am Mikrofon. Mit Erfahrung
              aus Live-Radio, Werbung, TV-Produktionen, Kino-Spots und Unternehmens-Kommunikation
              entstehen Ansagen, die nicht nach Vorlage klingen — sondern nach Persönlichkeit.
            </p>

            <div className="grid grid-cols-3 gap-6 mb-10">
              {[
                { v: "30+", l: "Jahre Mikrofon" },
                { v: "5.000+", l: "Produktionen" },
                { v: "2004", l: "Studio gegründet" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="display text-3xl sm:text-4xl text-[#d4a154]">{s.v}</div>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-white/45 mt-1">{s.l}</div>
                </div>
              ))}
            </div>

            <p className="text-xs tracking-[0.22em] uppercase text-white/40 mb-5">Referenzen — Auszug</p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-5">
              {CLIENTS.map((c) => (
                <img
                  key={c.name}
                  src={c.src}
                  alt={c.name}
                  loading="lazy"
                  className="h-7 sm:h-8 object-contain opacity-50 hover:opacity-90 transition-opacity"
                  style={{ filter: "grayscale(1) brightness(1.4) contrast(0.9)" }}
                />
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] max-w-lg mx-auto rounded-3xl overflow-hidden border border-white/[0.08]">
              <img
                src={SPEAKER_PORTRAIT}
                alt="Thomas Dickenbrok im Tonstudio Herford"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================ 5. DEMOS ============================ */}
      <section id="hoerbeispiele" className="border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Hörbeispiele</p>
            <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05]">
              So kann Ihre Ansage<br /><span className="italic">klingen.</span>
            </h2>
            <p className="text-base text-white/60 leading-relaxed mt-5">
              Echte Studio-Takes. TV, Kino, Radio, Werbung und Unternehmens-Ansagen — produziert von Thomas Dickenbrok in Herford.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {DEMOS.map((d) => (
              <WaveformPlayer key={d.key} demo={d} isActive={activeKey === d.key} onPlay={setActiveKey} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 6. PROCESS ============================ */}
      <section className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Ablauf</p>
            <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05]">
              In vier Schritten<br /><span className="italic">zur fertigen Ansage.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="relative pl-7 sm:pl-0">
                <div className="display text-[#d4a154]/30 text-7xl leading-none mb-4 select-none">{s.n}</div>
                <s.icon className="h-5 w-5 text-[#d4a154] mb-3" strokeWidth={1.6} />
                <h3 className="text-white font-medium text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 7. SEO/GEO COMPARE ============================ */}
      <section className="border-t border-white/[0.06] bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Was ist der Unterschied?</p>
          <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.08] mb-10">
            Mailboxansage bestellen oder Telefonansage bestellen?
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-white/[0.06] rounded-2xl overflow-hidden mb-10">
            <div className="bg-[#0a0a0a] p-7">
              <h3 className="display text-2xl text-white mb-3">Mailboxansage</h3>
              <p className="text-sm text-white/65 leading-relaxed">
                Eine kurze, persönliche Ansage für Mobilbox, Voicemail oder Smartphone. Sie wirkt sofort
                professionell — auch dann, wenn Sie gerade nicht erreichbar sind. Lieferung als MP3,
                einsatzbereit für alle Mobilfunkanbieter.
              </p>
            </div>
            <div className="bg-[#0a0a0a] p-7">
              <h3 className="display text-2xl text-white mb-3">Telefonansage</h3>
              <p className="text-sm text-white/65 leading-relaxed">
                Eine professionelle Begrüßung für Unternehmen, Praxen, Büros oder Telefonanlagen — inklusive
                Auswahlmenü (IVR), Warteschleife oder Außer-Haus-Ansage. Geeignet für VoIP, SIP, klassische
                TK-Anlagen und alle gängigen Telefonanbieter.
              </p>
            </div>
          </div>
          <p className="text-base text-white/65 leading-relaxed">
            <strong className="text-white">VOICE.NRW produziert beides:</strong> individuell eingesprochen
            von Thomas Dickenbrok im Tonstudio in Herford. Sendefähig, persönlich und ohne Massenware-Charakter.
          </p>
        </div>
      </section>

      {/* ============================ 8. FAQ ============================ */}
      <section className="border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="mb-14">
            <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-4">Häufige Fragen</p>
            <h2 className="display text-4xl sm:text-5xl font-medium leading-[1.05]">
              Was Kunden<br /><span className="italic">vor der Bestellung fragen.</span>
            </h2>
          </div>
          <div className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
            {FAQS.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} testId={`faq-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 9. FINAL CTA ============================ */}
      <section className="border-t border-white/[0.06] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-[0.08]"
          style={{ background: "radial-gradient(circle at 50% 50%, #d4a154 0%, transparent 60%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32 text-center">
          <p className="text-xs tracking-[0.22em] uppercase text-[#d4a154] mb-5">Bereit?</p>
          <h2 className="display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] mb-7">
            Bestellen Sie Ihre Ansage<br /><span className="italic">vom Profi.</span>
          </h2>
          <p className="text-lg text-white/65 leading-relaxed max-w-2xl mx-auto mb-10">
            Jeder Anruf ist ein Kontaktpunkt. Sorgen Sie dafür, dass Ihre Ansage genauso professionell klingt wie Ihr Unternehmen.
          </p>
          <a
            href={ORDER_URL}
            data-testid="final-cta-order"
            className="inline-flex items-center gap-2 bg-[#d4a154] text-[#0a0a0a] font-medium px-8 py-4 rounded-full hover:bg-[#e2b163] transition-colors text-[15px]"
          >
            Jetzt Ansage bestellen <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Slim legal footer — no public-nav, no sitemap */}
      <footer className="border-t border-white/[0.06] py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-4 text-xs text-white/40">
          <div className="inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#d4a154]" />
            <span className="tracking-[0.2em] uppercase">© VOICE.NRW · Tonstudio Herford</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="https://voicenrw.de/impressum" className="hover:text-white">Impressum</a>
            <a href="https://voicenrw.de/datenschutz" className="hover:text-white">Datenschutz</a>
            <a href="tel:+4915560055255" className="inline-flex items-center gap-2 hover:text-white">
              <Phone className="h-3.5 w-3.5" /> +49 1556 0055255
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left flex items-start justify-between gap-6 py-6 hover:opacity-90 transition-opacity"
      >
        <span className="text-lg sm:text-xl text-white font-medium leading-snug">{q}</span>
        <span
          className={`flex-shrink-0 h-6 w-6 rounded-full border border-white/20 flex items-center justify-center mt-1 transition-transform ${
            open ? "rotate-45 border-[#d4a154]" : ""
          }`}
          aria-hidden="true"
        >
          <span className="block h-3 w-px bg-current" />
          <span className="block w-3 h-px bg-current -ml-3" />
        </span>
      </button>
      {open && (
        <div className="pb-6 pr-10 text-base text-white/65 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
